import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './styles.module.scss';
import classNames from 'classnames/bind';
import { TAddress } from '@xeppt/xeppt-sdk/types/user';
import { Icon } from '@components/icons';
import Typography from '@components/common/typography';
import { defaultAddressData } from '@const/default_form_data';
import { useLocales } from '@hooks/helpers/useLocales';
import { showToast } from '@components/toast';
import { CanadaPostFindResponse, CanadaPostRetrieveResponse } from 'src/types/canada_post';
import { highlightSearchText } from '@utils/index';

interface IProps {
    onChange?: (value: TAddress) => void;
    label?: string;
    full?: boolean;
    placeholder?: string;
    className?: string;
    error?: boolean;
    isHelper?: boolean;
    helperText?: string;
    disabled?: boolean;
    value?: TAddress;
}

interface AddressSuggestion {
    id: string;
    text: string;
    description: string;
    next?: 'Find' | 'Retrieve';
}

const cx = classNames.bind(styles);

const CanadaPostAutocomplete: FC<IProps> = ({
    onChange,
    label,
    isHelper = true,
    helperText = 'autocomplete_helper',
    placeholder = 'Start typing address...',
    full,
    error,
    className,
    disabled,
    value
}) => {
    const { labelLocale } = useLocales();
    const [inputValue, setInputValue] = useState('');
    const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [inputId] = useState(String(Math.random()));
    const inputRef = useRef<HTMLInputElement>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);
    const debounceTimeout = useRef<NodeJS.Timeout>();

    const rootStyles = useMemo(
        () => cx([styles.root, className, { disabled, full, isLeftIcon: true, error }]),
        [full, error, disabled, className]
    );

    const labelStyles = useMemo(
        () => cx([styles.label, { isActive: !!inputValue || !!value }]),
        [inputValue, value]
    );

    // Canada Post API Service Methods
    const findAddresses = async (
        searchText: string,
        lastId?: string,
        country = 'CAN'
    ): Promise<CanadaPostFindResponse> => {
        return new Promise((resolve, reject) => {
            const baseUrl =
                'https://ws1.postescanada-canadapost.ca/AddressComplete/Interactive/Find/v2.10/json3.ws';
            let params = '';
            params += '&Key=' + encodeURIComponent(process.env.REACT_APP_CANADA_POST_API_KEY || '');
            params += '&SearchTerm=' + encodeURIComponent(searchText);
            params += '&Country=' + encodeURIComponent('CAN');
            params += '&LanguagePreference=' + encodeURIComponent('EN');
            params += '&MaxSuggestions=' + encodeURIComponent(10);
            params += '&MaxResults=' + encodeURIComponent(10);
            if (lastId) {
                params += '&LastId=' + encodeURIComponent(lastId);
            }

            const http = new XMLHttpRequest();
            http.open('POST', baseUrl, true);
            http.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');

            http.onreadystatechange = function () {
                if (http.readyState == 4) {
                    if (http.status == 200) {
                        const response = JSON.parse(http.responseText);
                        // Test for an error
                        if (
                            response.Items.length == 1 &&
                            typeof response.Items[0].Error != 'undefined'
                        ) {
                            reject(new Error(response.Items[0].Description));
                        } else {
                            resolve(response);
                        }
                    } else {
                        reject(new Error(`HTTP Error: ${http.status}`));
                    }
                }
            };

            http.send(params);
        });
    };

    const retrieveAddress = async (id: string): Promise<CanadaPostRetrieveResponse> => {
        return new Promise((resolve, reject) => {
            const baseUrl =
                'https://ws1.postescanada-canadapost.ca/AddressComplete/Interactive/Retrieve/v2.11/json3.ws';
            let params = '';
            params += '&Key=' + encodeURIComponent(process.env.REACT_APP_CANADA_POST_API_KEY || '');
            params += '&LanguagePreference=' + encodeURIComponent('EN');
            params += '&Id=' + encodeURIComponent(id);

            const http = new XMLHttpRequest();
            http.open('POST', baseUrl, true);
            http.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');

            http.onreadystatechange = function () {
                if (http.readyState == 4) {
                    if (http.status == 200) {
                        const response = JSON.parse(http.responseText);

                        // Test for an error (API returns error in this format)
                        if (
                            response.Items.length == 1 &&
                            typeof response.Items[0].Error != 'undefined'
                        ) {
                            reject(new Error(response.Items[0].Description));
                        } else if (response.Items.length == 0) {
                            reject(new Error('No address found'));
                        } else {
                            // Success - resolve with the response
                            resolve(response);
                        }
                    } else {
                        reject(new Error(`HTTP Error: ${http.status}`));
                    }
                }
            };

            http.send(params);
        });
    };

    // Transform Canada Post response to TAddress format
    const prettifyCanadaPostAddress = (
        canadaPostData: CanadaPostRetrieveResponse
    ): TAddress | undefined => {
        if (!canadaPostData.Items || canadaPostData.Items.length === 0) {
            return undefined;
        }

        const item = canadaPostData.Items[0];

        // Validate required fields
        if (!item.City || !item.ProvinceName || !item.PostalCode || !item.Street) {
            return undefined;
        }

        const address: TAddress = {
            address_1: `${item.BuildingNumber ? item.BuildingNumber + ' ' : ''}${item.Street}`,
            address_2: item.SubBuilding || '',
            city: item.City,
            region: item.ProvinceName,
            zip_code: item.PostalCode,
            country: item.CountryName || 'Canada'
        };

        return address;
    };

    const formatAddressForDisplay = (address: TAddress): string => {
        const parts = [
            address.address_1,
            address.address_2,
            address.city,
            address.region,
            address.zip_code,
            address.country
        ].filter(Boolean);

        return parts.join(', ');
    };

    const debouncedSearch = useCallback((searchText: string, lastId?: string) => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(async () => {
            if (searchText.length < 3) {
                setSuggestions([]);
                setShowSuggestions(false);
                return;
            }

            setIsLoading(true);
            try {
                const response = await findAddresses(searchText, lastId);
                const formattedSuggestions = response.Items.map((item) => ({
                    id: item.Id,
                    text: item.Text,
                    description: item.Description,
                    next: item.Next
                }));
                setSuggestions(formattedSuggestions);
                setShowSuggestions(formattedSuggestions.length > 0);
            } catch (error) {
                console.error('Canada Post API error:', error);
                showToast.error('Failed to fetch address suggestions');
                setSuggestions([]);
                setShowSuggestions(false);
            } finally {
                setIsLoading(false);
            }
        }, 1000);
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        debouncedSearch(newValue);
    };

    const handleSuggestionClick = async (suggestion: AddressSuggestion) => {
        setIsLoading(true);
        try {
            const response = await retrieveAddress(suggestion.id);
            const addressData = prettifyCanadaPostAddress(response);
            if (addressData) {
                setInputValue(formatAddressForDisplay(addressData));
                if (onChange) {
                    onChange(addressData);
                }
            } else {
                showToast.error('Unable to retrieve complete address information');
            }
        } catch (error) {
            console.error('Canada Post retrieve error:', error);
            showToast.error('Failed to retrieve address details');
        } finally {
            setIsLoading(false);
            setShowSuggestions(false);
        }
    };

    const handleClear = () => {
        setInputValue('');
        setSuggestions([]);
        setShowSuggestions(false);
        if (onChange) {
            onChange(defaultAddressData);
        }
    };

    const handleInputFocus = () => {
        if (suggestions.length > 0) {
            setShowSuggestions(true);
        }
    };

    // Update input value when value prop changes
    useEffect(() => {
        if (value && value.address_1 && value.city && value.region && value.zip_code) {
            const displayValue = formatAddressForDisplay(value);
            if (displayValue !== inputValue) {
                setInputValue(displayValue);
            }
        } else if (!value || Object.values(value).every((v) => !v)) {
            setInputValue('');
        }
    }, [value]);

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                suggestionsRef.current &&
                !suggestionsRef.current.contains(event.target as Node) &&
                inputRef.current &&
                !inputRef.current.contains(event.target as Node)
            ) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, []);

    console.log({ label });

    return (
        <div className={rootStyles}>
            <label htmlFor={inputId} className={labelStyles}>
                {label ? labelLocale(label) : 'Enter full address'}
            </label>
            <div className={`${styles.input_wrapper} ${disabled ? styles.disabled : ''}`}>
                {!disabled && (
                    <button disabled={disabled}>
                        <Icon width={48} height={48} name="search" className={styles.left_icon} />
                    </button>
                )}
                <input
                    ref={inputRef}
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    readOnly={disabled}
                    className={`${styles.input} ${disabled ? styles.disabled : ''}`}
                    placeholder={placeholder}
                    id={inputId}
                    type="text"
                    disabled={disabled}
                    autoComplete="off"
                />
                {!disabled && inputValue && (
                    <Icon
                        width={20}
                        height={20}
                        name="filed_close"
                        className={styles.right_icon}
                        onClick={handleClear}
                    />
                )}
                {isLoading && (
                    <div className={styles.loading_icon}>
                        <Icon width={16} height={16} name="refresh" />
                    </div>
                )}
            </div>

            {showSuggestions && suggestions.length > 0 && (
                <div ref={suggestionsRef} className="cp-container">
                    {suggestions.map((suggestion) => (
                        <div
                            key={suggestion.id}
                            className="cp-item"
                            onClick={() => {
                                if (suggestion?.next === 'Find') {
                                    debouncedSearch(inputValue, suggestion.id);
                                } else {
                                    handleSuggestionClick(suggestion);
                                }
                            }}>
                            <span></span>
                            <Typography
                                variant="body4"
                                className="cp-item_query"
                                dangerouslySetInnerHTML={{
                                    __html: highlightSearchText(
                                        `${suggestion.text} ${suggestion.description}`,
                                        inputValue
                                    )
                                }}
                            />
                        </div>
                    ))}
                </div>
            )}

            {isHelper && helperText && (
                <Typography
                    variant="body3"
                    className={error ? styles.error_text : styles.helper_text}>
                    {helperText === 'autocomplete_helper' ? labelLocale(helperText) : helperText}
                </Typography>
            )}
        </div>
    );
};

export default CanadaPostAutocomplete;
